import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Gift,
  Copy,
  Users,
  CheckCircle2,
  Sparkles,
  Share2,
  Loader2,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const ReferralCard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/referrals/me`, { withCredentials: true });
        setData(res.data);
      } catch (e) {
        console.error("ReferralCard load error:", e);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) {
    return (
      <div className="luxury-card p-6 flex items-center gap-3 text-slate-400">
        <Loader2 className="w-4 h-4 animate-spin" /> Carregando indicações…
      </div>
    );
  }

  if (!data) return null;

  const fullLink = `${window.location.origin}${data.referral_link}`;

  const copy = async (text, label) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${label} copiado!`);
    } catch {
      toast.error("Não foi possível copiar.");
    }
  };

  const share = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: "Lojas Integradas — SaaS para Lojas Maçônicas",
          text: `Use meu código de indicação (${data.referral_code}) e digitalize sua Loja com o Lojas Integradas.`,
          url: fullLink,
        });
      } catch {
        // user cancelled
      }
    } else {
      copy(fullLink, "Link");
    }
  };

  const conversionRate =
    data.total_signups > 0
      ? Math.round((data.paying_conversions / data.total_signups) * 100)
      : 0;

  return (
    <div
      data-testid="referral-card"
      className="luxury-card p-6 border-l-4 border-l-emerald-500 bg-gradient-to-br from-emerald-950/20 to-transparent"
    >
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div className="flex items-start gap-4">
          <div className="w-11 h-11 rounded-xl bg-emerald-500/15 text-emerald-300 flex items-center justify-center">
            <Gift className="w-5 h-5" />
          </div>
          <div>
            <div className="text-xs uppercase tracking-widest text-emerald-300 font-bold">
              Programa de Indicação
            </div>
            <h3 className="text-xl font-serif font-bold text-white mt-1">
              1 mês grátis por cada Loja indicada
            </h3>
            <p className="text-xs text-slate-400 mt-1 max-w-md leading-relaxed">
              Compartilhe seu link. Quando a Loja indicada assinar um plano pago,
              você ganha 1 mês adicionado ao saldo.
            </p>
          </div>
        </div>
      </div>

      {/* Balance highlight */}
      <div className="mt-6 grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Stat
          testid="ref-stat-balance"
          value={data.free_months_balance}
          label="Meses grátis"
          accent="text-emerald-300"
          suffix={data.free_months_balance === 1 ? "" : ""}
          icon={Sparkles}
          highlight
        />
        <Stat
          testid="ref-stat-signups"
          value={data.total_signups}
          label="Cadastros"
          accent="text-[#D4AF37]"
          icon={Users}
        />
        <Stat
          testid="ref-stat-paying"
          value={data.paying_conversions}
          label="Convertidos"
          accent="text-cyan-300"
          icon={CheckCircle2}
        />
        <Stat
          testid="ref-stat-conversion"
          value={`${conversionRate}%`}
          label="Conversão"
          accent="text-purple-300"
          icon={Sparkles}
        />
      </div>

      {/* Link */}
      <div className="mt-6">
        <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold mb-2">
          Seu link de indicação
        </div>
        <div className="flex flex-col sm:flex-row gap-2">
          <input
            data-testid="ref-link-input"
            readOnly
            value={fullLink}
            className="flex-1 px-4 py-2.5 rounded-xl bg-slate-900/60 border border-slate-800 font-mono text-xs text-slate-200 truncate"
          />
          <button
            data-testid="ref-copy-link"
            onClick={() => copy(fullLink, "Link")}
            className="flex items-center justify-center gap-2 bg-slate-800 hover:bg-slate-700 text-slate-100 font-bold px-4 py-2.5 rounded-xl text-xs uppercase tracking-wider transition-all"
          >
            <Copy className="w-3.5 h-3.5" /> Copiar
          </button>
          <button
            data-testid="ref-share"
            onClick={share}
            className="flex items-center justify-center gap-2 bg-emerald-500 hover:bg-emerald-600 text-white font-bold px-4 py-2.5 rounded-xl text-xs uppercase tracking-wider transition-all"
          >
            <Share2 className="w-3.5 h-3.5" /> Compartilhar
          </button>
        </div>
        <div className="mt-3 text-xs text-slate-500">
          Ou peça para usarem o código:{" "}
          <button
            data-testid="ref-copy-code"
            onClick={() => copy(data.referral_code, "Código")}
            className="font-mono font-bold text-[#D4AF37] hover:underline"
          >
            {data.referral_code}
          </button>
        </div>
      </div>

      {/* Recent referrals */}
      {data.referred_lojas?.length > 0 && (
        <div className="mt-6 pt-6 border-t border-slate-800/60">
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold mb-3">
            Lojas que você indicou
          </div>
          <div className="space-y-2">
            {data.referred_lojas.slice(0, 4).map((l) => (
              <div
                key={l.id}
                data-testid={`ref-loja-${l.id}`}
                className="flex items-center justify-between py-2 px-3 rounded-lg bg-slate-900/40"
              >
                <div>
                  <div className="text-sm text-white font-medium">{l.name}</div>
                  <div className="text-[10px] text-slate-500 font-mono">nº {l.number}</div>
                </div>
                <span
                  className={`text-[10px] uppercase tracking-widest font-bold px-2 py-1 rounded ${
                    l.plan_id && l.plan_id !== "free"
                      ? "bg-emerald-500/15 text-emerald-300"
                      : "bg-slate-800 text-slate-400"
                  }`}
                >
                  {l.plan_id && l.plan_id !== "free" ? "Convertido" : "Aguardando"}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const Stat = ({ testid, icon: Icon, value, label, accent, highlight }) => (
  <div
    data-testid={testid}
    className={`rounded-xl p-3 border ${
      highlight ? "bg-emerald-500/10 border-emerald-500/30" : "bg-slate-900/40 border-slate-800"
    }`}
  >
    <div className={`w-7 h-7 rounded-lg bg-slate-800/70 flex items-center justify-center ${accent}`}>
      <Icon className="w-3.5 h-3.5" />
    </div>
    <div className={`mt-2 text-2xl font-serif font-bold ${accent}`}>{value}</div>
    <div className="text-[10px] uppercase tracking-widest text-slate-500">{label}</div>
  </div>
);

export default ReferralCard;
